import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Unit, UnitLabel } from '@/shared/config/config'
import {
  Alert,
  SelectGroup,
  CheckboxGroup,
  Button,
  NumericInputGroup,
  GenericDialog
} from '@/common'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import {
  ElectricityProductionMethodConfig,
  electricityProductionMethodConfigSchema,
  electricityRenewableProductionMethodConfigSchema
} from '@/api/EmissionReportModel'
import { ElectricityProductionMethod } from '@/common'
import { useEmissionFactorValidation } from '@/hooks/useEmissionFactorValidation'
import { convertMwhToTj, convertTjToMwh } from '@/utils'
import { usePermissions } from '@/shared/hooks/usePermissions'

const getInitialValues = (
  initialValues: ElectricityProductionMethodConfig,
  isRenewable: boolean,
  defaultGridEmissionFactor: number | null
) => {
  const getInitialEmissionFactor = () => {
    if (isRenewable) {
      return {
        emissionFactor: 0,
        isEmissionFactorDefault: false
      }
    }

    if (initialValues?.emissionFactor) {
      return {
        emissionFactor: initialValues.emissionFactor,
        isEmissionFactorDefault: initialValues.isEmissionFactorDefault
      }
    }

    if (defaultGridEmissionFactor) {
      return {
        emissionFactor: defaultGridEmissionFactor,
        isEmissionFactorDefault: true
      }
    }

    return {
      emissionFactor: initialValues?.emissionFactor || '',
      isEmissionFactorDefault: false
    }
  }

  return {
    fuelType: initialValues?.fuelType || '',
    consumption: initialValues?.consumption || 0,
    consumptionUnit: initialValues?.consumptionUnit || null,
    isRenewable: isRenewable || false,
    consumptionMwh: convertTjToMwh(initialValues?.consumption || 0),
    ...getInitialEmissionFactor()
  }
}

export default function ElectricitySourceDialog({
  defaultGridEmissionFactor,
  isRenewable,
  method,
  close,
  save,
  isSaving,
  initialValues,
  isOpen,
  onAfterLeave
}: {
  defaultGridEmissionFactor: number | null
  isRenewable: boolean
  initialValues: ElectricityProductionMethodConfig | null
  method: ElectricityProductionMethod
  close: () => void
  isOpen: boolean
  save: (method: ElectricityProductionMethod, payload: ElectricityProductionMethodConfig) => void
  isSaving: boolean
  onAfterLeave: () => void
}) {
  const { combustionFuelTypesById } = useAppConfig().config
  const { t } = useTranslation()
  const { checkPermission } = usePermissions()

  const showEmissionFactorCheckbox = method === ElectricityProductionMethod.GRID
  const showEmissionFactorCallout =
    initialValues?.isEmissionFactorDefault && method === ElectricityProductionMethod.GRID

  const [state, setState] = useState(
    getInitialValues(initialValues!, isRenewable, defaultGridEmissionFactor)
  )

  const {
    fuelType,
    emissionFactor,
    consumption,
    isEmissionFactorDefault,
    consumptionUnit,
    consumptionMwh
  } = state

  const { useEmissionFactorIsOutOfRangeValidation } = useEmissionFactorValidation({
    fuelType: fuelType || ''
  })

  const [emissionFactorWarning, setEmissionFactorWarning] = useState<string>('')

  const onEmissionFactorChange = useCallback(
    (emissionFactor: string) => {
      setState(prev => ({ ...prev, emissionFactor: Number(emissionFactor) }))
      const numberEmissionFactor = Number(emissionFactor)
      const validateData = useEmissionFactorIsOutOfRangeValidation.safeParse(numberEmissionFactor)
      if (!validateData.success && numberEmissionFactor > 0) {
        const warningMessage = validateData.error?.issues[0].message
        setEmissionFactorWarning(warningMessage)
      } else {
        setEmissionFactorWarning('')
      }
    },
    [useEmissionFactorIsOutOfRangeValidation]
  )

  const onCustomEmissionCheckedChange = useCallback(
    (checked: boolean) => {
      const selectedFuelTypeEmissionFactor = fuelType
        ? combustionFuelTypesById[fuelType]?.emissionFactor
        : null
      if (!checked) {
        if (selectedFuelTypeEmissionFactor) {
          setState({
            ...state,
            emissionFactor: selectedFuelTypeEmissionFactor,
            isEmissionFactorDefault: true
          })
        } else if (defaultGridEmissionFactor) {
          setState({
            ...state,
            emissionFactor: defaultGridEmissionFactor,
            isEmissionFactorDefault: true
          })
        } else {
          setState({
            ...state,
            emissionFactor: 0,
            isEmissionFactorDefault: true
          })
        }
      } else {
        setState({
          ...state,
          isEmissionFactorDefault: false,
          emissionFactor: selectedFuelTypeEmissionFactor ? selectedFuelTypeEmissionFactor : 0
        })
      }
    },
    [combustionFuelTypesById, fuelType, state]
  )

  const parsedState = isRenewable
    ? electricityRenewableProductionMethodConfigSchema.safeParse({
        fuelType,
        consumption,
        consumptionUnit,
        isRenewable,
        emissionFactor,
        isEmissionFactorDefault,
        type: method
      })
    : electricityProductionMethodConfigSchema.safeParse({
        fuelType,
        consumption,
        consumptionUnit,
        isRenewable,
        emissionFactor,
        isEmissionFactorDefault,
        type: method
      })

  const onConsumptionMwhChange = useCallback(
    (value: string) => {
      if (consumptionUnit === Unit.MWh) {
        const updatedConsumption = Number(value) || 0

        setState(prev => ({
          ...prev,
          consumption: convertMwhToTj(updatedConsumption),
          consumptionMwh: updatedConsumption
        }))
      }
    },
    [consumptionUnit]
  )

  const onConsumptionTjChange = useCallback(
    (value: string) => {
      if (consumptionUnit === Unit.TJ) {
        const updatedConsumption = Number(value) || 0
        setState(prev => ({
          ...prev,
          consumption: updatedConsumption,
          consumptionMwh: convertTjToMwh(updatedConsumption)
        }))
      }
    },
    [consumptionUnit]
  )

  const onConsumptionUnitChange = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      consumptionUnit: value as Unit.TJ | Unit.MWh,
      consumptionMwh: 0,
      consumption: 0
    }))
  }, [])

  const onSaveClick = useCallback(async () => {
    if (!parsedState.success) {
      return
    }

    await save(method, parsedState.data)

    close()
  }, [save, method, parsedState, close])
  const canEditReport = checkPermission('emissionReport', 'update')

  const isNextButtonDisabled = !parsedState.success || isSaving || !canEditReport

  useEffect(() => {
    if (initialValues) {
      setState(getInitialValues(initialValues, isRenewable, defaultGridEmissionFactor))
    }
  }, [initialValues, isRenewable, defaultGridEmissionFactor])

  return (
    <GenericDialog
      size="md"
      title={t('report:electricity.electricitySourceDialog.title')}
      subtitle={t(`common:electricitySources.${method}.title`)}
      isOpen={isOpen}
      close={close}
      onAfterLeave={onAfterLeave}
    >
      <div className="space-y-4">
        <div className="space-y-4">
          {showEmissionFactorCallout && (
            <Alert type="info">
              {t('report:electricity.callout.defaultEmissionFactor', {
                emissionFactor: defaultGridEmissionFactor,
                unit: UnitLabel.tCO2PerTJ
              })}
            </Alert>
          )}
          {isRenewable && (
            <Alert type="info">{t('report:electricity.callout.renewableElectricity')}</Alert>
          )}
          {!consumptionUnit && (
            <Alert type="info">{t('report:electricity.callout.consumptionUnit')}</Alert>
          )}
          <SelectGroup
            label={t('report:electricity.fields.consumptionUnit.label')}
            value={consumptionUnit || ''}
            onChange={evt => onConsumptionUnitChange(evt.target.value)}
            options={[
              { value: Unit.TJ, label: UnitLabel.TJ },
              { value: Unit.MWh, label: UnitLabel.MWh }
            ]}
          />
          <NumericInputGroup
            label={t('report:electricity.fields.consumptionTj.label')}
            tooltip={t('report:electricity.fields.consumptionTj.tooltip')}
            value={consumption !== undefined ? consumption.toString() : ''}
            suffix={UnitLabel.TJ}
            disabled={consumptionUnit === Unit.MWh || !consumptionUnit}
            numericFormat={{
              // Using the special `onValueChange` prop from the react-number-format library
              // to have access to the value without the thousand separators
              onValueChange: ({ value }) => {
                onConsumptionTjChange(value)
              },
              decimalScale: 3,
              allowNegative: false,
              thousandSeparator: ','
            }}
          />
          <NumericInputGroup
            value={consumptionMwh !== undefined ? consumptionMwh.toString() : ''}
            suffix={UnitLabel.MWh}
            disabled={consumptionUnit === Unit.TJ || !consumptionUnit}
            numericFormat={{
              // Using the special `onValueChange` prop from the react-number-format library
              // to have access to the value without the thousand separators
              onValueChange: ({ value }) => {
                onConsumptionMwhChange(value)
              },
              decimalScale: 3,
              allowNegative: false,
              thousandSeparator: ','
            }}
          />

          {showEmissionFactorCheckbox && (
            <CheckboxGroup
              id="useCustomEmissionFactor"
              label={t('report:electricity.fields.useCustomEmissionFactor.label')}
              disabled={isRenewable || !consumptionUnit}
              checked={!isEmissionFactorDefault}
              onChange={onCustomEmissionCheckedChange}
            />
          )}
          <NumericInputGroup
            label={t('report:electricity.fields.emissionFactorCo2PTJ.label')}
            disabled={isRenewable || isEmissionFactorDefault || !consumptionUnit}
            value={emissionFactor?.toString()}
            warning={emissionFactorWarning}
            onChange={evt => onEmissionFactorChange(evt.target.value)}
            suffix={UnitLabel.tCO2ePerMWh}
            numericFormat={{
              decimalScale: 5,
              allowNegative: false
            }}
          />
        </div>
        <footer className="flex justify-end gap-2">
          <Button className="min-w-24" variant="neutral" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            className="min-w-24"
            onClick={onSaveClick}
            isLoading={isSaving}
            disabled={isNextButtonDisabled}
          >
            {t('common:actions.save')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
